import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col, Nav, Tab, Badge, InputGroup } from 'react-bootstrap';
import { FiAlertTriangle, FiRefreshCw, FiEye, FiEdit, FiTrash2, FiGift, FiCheckCircle, FiCalendar, FiLink, FiDownload, FiSettings, FiCopy, FiCheck, FiFileText, FiExternalLink, FiUpload, FiMail, FiCloud } from 'react-icons/fi';
import { fetchTripsClaimsRequest, fetchTripsCheckClaimsRequest } from '../../store/actions/tripsActions';
import { deleteClaimById, generateRibFormUrl, downloadS3File, getAllPartners, postTripsClientsBankDetailsBulkRemindersEmail, getTripsClaimsFullList } from '../../services/api';
import { flattenPoliciesWithClaimsForExcel, TRIPS_CLAIMS_EXPORT_COLUMNS } from '../../utils/tripsClaimsExcelRows';
import * as XLSX from 'xlsx';
import { handleApiError } from '../../utils/errorHandler';
import {
    generateBulkTransfersCsvRequest,
    generateBulkTransfersCsvByPolicyCodesRequest,
    generateBulkTransfersCsvEndOfStayRequest,
    generateBulkTransfersCsvEndOfStayByPolicyCodesRequest
} from '../../store/actions/dataActions';
import {
    importClaimsBulkUpdatesFlower,
    importClaimsBulkUpdatesOlela,
    importClaimsBulkUpdatesB2C,
    importClaimsBulkUpdatesGeneric,
    importClaimsBulkUpdatesStandard,
} from '../../services/api';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import { TripWeatherDetailsPanel } from '../../components/weather';
import StandardEndOfStayTransferPanel from './bulkTransfers/StandardEndOfStayTransferPanel';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsClaims.css';

const EMPTY_EXPORT_CLAIMS_FILTERS = {
    reservation_number: '',
    policy_code: '',
    client_email: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    claim_code: '',
    date_of_claim: '',
    claim_status: '',
    company_name: '',
    trip_modified: '',
    new_trip_start_date_from: '',
    new_trip_start_date_to: '',
    new_trip_end_date_from: '',
    new_trip_end_date_to: '',
};

const DDMMYYYY_CLAIMS_DATE_FILTER_KEYS = new Set(['activity_end_date_from', 'activity_end_date_to']);

const TRIP_CLAIM_STATUS_OPTIONS = [
    'Pre-validated',
    'Not pre-validated',
    'Validated',
    'Expired',
    'Paid',
    'Not validated',
    'Documents received',
    'No answer yet',
];

/** Weather Details — GET /api/trips/claims (filtres optionnels) */
const fetchClaimsWeatherPayload = async ({ policy_code, reservation_number, client_email } = {}) => {
    const params = {};
    if (policy_code) params.policy_code = policy_code;
    if (reservation_number) params.reservation_number = reservation_number;
    if (client_email) params.client_email = client_email;
    return getTripsClaimsFullList(params);
};

const formatClaimAmount = (value) => {
    if (value == null || value === '') return '—';
    return String(value);
};

const parseAmountNumber = (value) => {
    if (value == null || value === '' || value === '—') return 0;
    const parsed = parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
};

const isZeroishAmount = (value) => parseAmountNumber(value) === 0;

const getClaimCoverageType = (claim) => {
    const trips = claim?.reference_info?.subcoverage_trips;
    const fromSubcoverage = Array.isArray(trips)
        ? trips.map((trip) => trip?.coverage_type).find((type) => type != null && String(type).trim() !== '')
        : null;
    return String(claim?.coverage_type || fromSubcoverage || '').trim().toLowerCase();
};

const claimUsesCancellationAmounts = (claim) => getClaimCoverageType(claim) === 'cancellation';

const getClaimTableAmounts = (claim) => {
    if (claimUsesCancellationAmounts(claim)) {
        return {
            amount: claim.amount_of_claim_cancellation,
            newAmount: claim.new_amount_of_claim_cancellation,
            cash: claim.cash_claim_amount_cancellation,
            newCash: claim.new_cash_claim_amount_cancellation,
            voucher: claim.voucher_claim_amount_cancellation,
            newVoucher: claim.new_voucher_claim_amount_cancellation,
        };
    }
    return {
        amount: claim.amount_of_claim,
        newAmount: claim.new_amount_of_claim,
        cash: claim.cash_claim_amount_maintain,
        newCash: claim.new_cash_claim_amount_maintain,
        voucher: claim.voucher_claim_amount_maintain,
        newVoucher: claim.new_voucher_claim_amount_maintain,
    };
};

const formatParsedAmount = (value) => (
    Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
);

const resolvePolicyAmountTotal = (item, keys, amountKey, fallback = '0') => {
    const claims = getClaimsFromBlock(item);
    const fromBlock = pickBlockField(item, ...keys);
    const isNewTotal = String(amountKey).startsWith('new');

    if (isNewTotal) {
        return fromBlock === '—' ? '—' : fromBlock;
    }

    if (!isZeroishAmount(fromBlock)) return fromBlock;

    const sum = claims.reduce((total, claim) => (
        total + parseAmountNumber(getClaimTableAmounts(claim)[amountKey])
    ), 0);
    if (sum > 0) return formatParsedAmount(sum);
    return fromBlock === '—' ? fallback : fromBlock;
};

const pickBlockField = (item, ...keys) => {
    for (const key of keys) {
        const value = item?.[key] ?? item?.policy_info?.[key];
        if (value != null && value !== '') {
            return formatClaimAmount(value);
        }
    }
    return '—';
};

const getActivityInfoFromBlock = (item) => {
    if (item?.activity_info && typeof item.activity_info === 'object') {
        return item.activity_info;
    }
    if (item?.policy_info?.activity_info && typeof item.policy_info.activity_info === 'object') {
        return item.policy_info.activity_info;
    }
    const claims = Array.isArray(item?.claims) ? item.claims : [];
    const claimWithActivity = claims.find((claim) => claim?.activity_info && typeof claim.activity_info === 'object');
    return claimWithActivity?.activity_info || {};
};

const getClaimsFromBlock = (item) => (Array.isArray(item?.claims) ? item.claims : []);

const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const resolveTripDateModified = (item) => {
    const activityInfo = getActivityInfoFromBlock(item);
    if (isTruthyFlag(activityInfo.trip_modified)) return true;
    if (isTruthyFlag(item?.policy_info?.trip_modified)) return true;
    if (isTruthyFlag(item?.trip_modified)) return true;
    return getClaimsFromBlock(item).some(
        (claim) => isTruthyFlag(claim?.activity_info?.trip_modified) || isTruthyFlag(claim?.trip_modified)
    );
};

const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value != null && value !== '') return value;
    }
    return '';
};

const resolveForecastTripDay = (item) => {
    for (const claim of getClaimsFromBlock(item)) {
        const tripDay = claim?.forecast_info?.trip_day;
        if (tripDay != null && tripDay !== '') return tripDay;
    }
    return '';
};

const resolveNewTripStartDate = (item) => {
    const activityInfo = getActivityInfoFromBlock(item);
    const fromActivity = pickFirstDefined(
        activityInfo.new_trip_start_date,
        item?.policy_info?.new_trip_start_date
    );
    if (fromActivity) return fromActivity;

    for (const claim of getClaimsFromBlock(item)) {
        const value = pickFirstDefined(claim?.activity_info?.new_trip_start_date);
        if (value) return value;
    }

    if (resolveTripDateModified(item)) {
        return resolveForecastTripDay(item);
    }
    return '';
};

const resolveNewTripEndDate = (item) => {
    const activityInfo = getActivityInfoFromBlock(item);
    const fromActivity = pickFirstDefined(
        activityInfo.new_trip_end_date,
        item?.policy_info?.new_trip_end_date
    );
    if (fromActivity) return fromActivity;

    for (const claim of getClaimsFromBlock(item)) {
        const value = pickFirstDefined(claim?.activity_info?.new_trip_end_date);
        if (value) return value;
    }
    return '';
};

const resolveClaimActivityInfo = (claim, block) => claim?.activity_info || getActivityInfoFromBlock(block);

const renderTripModifiedBadge = (value) => {
    const modified = isTruthyFlag(value);
    return (
        <Badge bg={modified ? 'warning' : 'secondary'}>
            {modified ? 'Oui' : 'Non'}
        </Badge>
    );
};

const TRIPS_CLAIMS_TABLE_COLSPAN = 14;

const CLAIM_STATUS_PAID_IMPORT_TEMPLATE_URL =
    'https://logos-partenaires.s3.eu-west-3.amazonaws.com/template_import_update_status_paid.xlsx';

const copyTextToClipboard = async (text, setCopied) => {
    try {
        await navigator.clipboard.writeText(text);
        if (setCopied) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            if (setCopied) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (fallbackErr) {
            console.error('Erreur fallback:', fallbackErr);
        }
        document.body.removeChild(textArea);
    }
};

const downloadBulkTransferFile = async (urlOrPath, filename) => {
    try {
        if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
            try {
                const response = await fetch(urlOrPath, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                });
                if (!response.ok) {
                    throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
                }
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (fetchError) {
                const link = document.createElement('a');
                link.href = urlOrPath;
                link.download = filename;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } else if (urlOrPath.startsWith('s3://')) {
            try {
                const response = await downloadS3File(urlOrPath);
                const blob = new Blob([response.data], {
                    type: response.headers['content-type'] || 'application/octet-stream'
                });
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (apiError) {
                alert('Le téléchargement via l\'API n\'est pas encore disponible. Le fichier est disponible dans S3 : ' + urlOrPath);
            }
        } else {
            throw new Error('Format d\'URL non supporté');
        }
    } catch (error) {
        console.error('Erreur lors du téléchargement:', error);
        alert('Erreur lors du téléchargement du fichier : ' + error.message);
    }
};

const FileLinkWithCopy = ({ label, link, totalRecords, showDownload, onDownload }) => {
    const [copied, setCopied] = useState(false);

    if (!link) return null;

    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-1">
                <span className="small fw-semibold">{label}</span>
                {totalRecords !== undefined && (
                    <span className="text-muted small">
                        ({totalRecords} enregistrement{totalRecords > 1 ? 's' : ''})
                    </span>
                )}
            </div>
            <div className="d-flex align-items-start gap-2 flex-wrap">
                <code className="small flex-grow-1" style={{ wordBreak: 'break-all' }}>{link}</code>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => copyTextToClipboard(link, setCopied)}
                    title="Copier le lien"
                    style={{ minWidth: '90px' }}
                >
                    {copied ? (
                        <>
                            <FiCheck className="me-1" />
                            Copié !
                        </>
                    ) : (
                        <>
                            <FiCopy className="me-1" />
                            Copier
                        </>
                    )}
                </Button>
                {link.startsWith('http') && (
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none small"
                    >
                        <FiExternalLink className="me-1" />
                        Ouvrir dans S3
                    </a>
                )}
                {showDownload && onDownload && (
                    <Button variant="outline-primary" size="sm" onClick={onDownload}>
                        <FiDownload className="me-1" />
                        Télécharger
                    </Button>
                )}
            </div>
        </div>
    );
};

const FileDownloadSection = ({ data }) => {
    if (!data) return null;

    let csvFile = null;
    let excelFile = null;

    if (data.files && Array.isArray(data.files)) {
        csvFile = data.files.find(file => file.type === 'CSV');
        excelFile = data.files.find(file => file.type === 'Excel');
    }

    const csvUrl = data.csv_url || data.csv_s3_url || data.transfers_csv_url || data.transfers_file_url;
    const excelUrl = data.excel_url || data.excel_s3_url || data.paid_daily_excel_url || data.paid_daily_file_url;

    const csvS3Path = data.csv_s3_path || data.transfers_csv_s3_path || data.transfers_s3_path ||
        (csvFile && csvFile.s3_path) || (csvFile && csvFile.s3_file_path);
    const excelS3Path = data.excel_s3_path || data.paid_daily_excel_s3_path || data.paid_daily_s3_path ||
        (excelFile && excelFile.s3_path) || (excelFile && excelFile.s3_file_path);

    const finalCsvFile = csvFile ? {
        ...csvFile,
        file_path: csvFile.file_path || csvFile.url || csvUrl,
        s3_path: csvFile.s3_path || csvFile.s3_file_path || csvS3Path || null,
        s3_console_url: csvFile.s3_console_url || null,
    } : (csvUrl ? {
        file_path: csvUrl,
        filename: csvUrl.split('/').pop() || 'transfers.csv',
        s3_path: csvS3Path || null,
        s3_console_url: null,
    } : null);

    const finalExcelFile = excelFile ? {
        ...excelFile,
        file_path: excelFile.file_path || excelFile.url || excelUrl,
        s3_path: excelFile.s3_path || excelFile.s3_file_path || excelS3Path || null,
        s3_console_url: excelFile.s3_console_url || null,
    } : (excelUrl ? {
        file_path: excelUrl,
        filename: excelUrl.split('/').pop() || 'paid_daily.xlsx',
        s3_path: excelS3Path || null,
        s3_console_url: null,
    } : null);

    if (!finalCsvFile && !finalExcelFile) {
        return null;
    }

    const getS3ConsoleUrl = (file) => file?.s3_console_url || null;

    const getDownloadUrl = (file) => {
        if (file.file_path && (file.file_path.startsWith('http://') || file.file_path.startsWith('https://'))) {
            return file.file_path;
        }
        if (file.s3_path) {
            return file.s3_path;
        }
        return file.file_path;
    };

    const getDisplayLink = (file) => getS3ConsoleUrl(file) || getDownloadUrl(file);

    const bucketConsoleUrl = data.s3_console_url || null;

    return (
        <div className="mt-3">
            <strong>Fichiers disponibles :</strong>
            {bucketConsoleUrl && (
                <div className="mt-2 mb-2">
                    <FileLinkWithCopy
                        label="Dossier S3"
                        link={bucketConsoleUrl}
                    />
                </div>
            )}
            <div className="d-flex flex-column gap-3 mt-2">
                {finalCsvFile && getDisplayLink(finalCsvFile) && (
                    <FileLinkWithCopy
                        label="Fichier banque (CSV)"
                        link={getDisplayLink(finalCsvFile)}
                        totalRecords={finalCsvFile.total_records}
                    />
                )}
                {finalExcelFile && getDisplayLink(finalExcelFile) && (
                    <FileLinkWithCopy
                        label="Fichier claim update status paid (Excel)"
                        link={getDisplayLink(finalExcelFile)}
                        totalRecords={finalExcelFile.total_records}
                        showDownload
                        onDownload={() => {
                            const downloadUrl = getDownloadUrl(finalExcelFile);
                            const filename = finalExcelFile.filename || downloadUrl.split('/').pop()?.split('?')[0] || 'paid_daily.xlsx';
                            downloadBulkTransferFile(downloadUrl, filename);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const AdminTripsClaims = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const claimsState = useSelector((state) => state.trips.claims);
    const checkClaimsState = useSelector((state) => state.trips.checkClaims);
    const [activeTab, setActiveTab] = useState('claims');

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('trips-claims', EMPTY_EXPORT_CLAIMS_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [expandedPolicies, setExpandedPolicies] = useState({});
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    // Sub-claim state
    const [showClaimDetailModal, setShowClaimDetailModal] = useState(false);
    const [showClaimEditModal, setShowClaimEditModal] = useState(false);
    const [showClaimDeleteModal, setShowClaimDeleteModal] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [selectedPolicyIdForClaim, setSelectedPolicyIdForClaim] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(null);
    const [exportCompanyPartnerSearch, setExportCompanyPartnerSearch] = useState('');

    const [exportClaimsFilters, setExportClaimsFilters] = useState(() => ({ ...EMPTY_EXPORT_CLAIMS_FILTERS }));
    const [exportClaimsLoading, setExportClaimsLoading] = useState(false);
    const [exportClaimsError, setExportClaimsError] = useState(null);

    // États pour l'onglet Trigger verification
    const [checkClaimsPage, setCheckClaimsPage] = useState(1);
    const [checkClaimsFilters, setCheckClaimsFilters] = useState({
        reservation_number: '',
        policy_code: '',
        client_email: '',
        activity_end_date_from: '',
        activity_end_date_to: '',
        claim_code: '',
        date_of_claim: '',
        company_name: '',
        reference_name: '',
    });
    const [checkClaimsAppliedFilters, setCheckClaimsAppliedFilters] = useState({});

    // États pour l'onglet Générer le lien RIB
    const [policyCode, setPolicyCode] = useState('');
    const [ribLoading, setRibLoading] = useState(false);
    const [ribError, setRibError] = useState(null);
    const [ribUrl, setRibUrl] = useState(null);
    const [ribInfo, setRibInfo] = useState(null);
    const [copied, setCopied] = useState(false);

    // États pour l'onglet Generate Bank Transfer File
    const [bulkTransfersActiveTab, setBulkTransfersActiveTab] = useState('daily');
    const [bulkTransfersMethod, setBulkTransfersMethod] = useState('byDate');
    const [claimDate, setClaimDate] = useState('');
    const [policyCodes, setPolicyCodes] = useState('');
    const [claimDateByCodes, setClaimDateByCodes] = useState('');
    const [tripEndDateFrom, setTripEndDateFrom] = useState('');
    const [tripEndDateTo, setTripEndDateTo] = useState('');
    const [policyCodesEndOfStay, setPolicyCodesEndOfStay] = useState('');

    // États pour l'onglet Generate Bank Transfer File
    const {
        generateBulkTransfersCsv,
        generateBulkTransfersCsvByPolicyCodes,
        generateBulkTransfersCsvEndOfStay,
        generateBulkTransfersCsvEndOfStayByPolicyCodes
    } = useSelector((state) => state.data);

    // États pour l'onglet Update Claim Status
    const [importFile, setImportFile] = useState(null);
    const [claimsManagementImportType, setClaimsManagementImportType] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(null);

    const [forecastPartners, setForecastPartners] = useState([]);
    const [forecastPartnersLoading, setForecastPartnersLoading] = useState(false);
    const [forecastPartnersError, setForecastPartnersError] = useState(null);

    // Onglet import paiements générique (tous partenaires)
    const [genericPaymentFile, setGenericPaymentFile] = useState(null);
    const [genericPaymentCompanyNames, setGenericPaymentCompanyNames] = useState([]);
    const [genericPaymentCompanySearch, setGenericPaymentCompanySearch] = useState('');
    const [genericPaymentLoading, setGenericPaymentLoading] = useState(false);
    const [genericPaymentError, setGenericPaymentError] = useState(null);
    const [genericPaymentSuccess, setGenericPaymentSuccess] = useState(null);

    // Onglet import paiements Standard
    const [standardPaymentFile, setStandardPaymentFile] = useState(null);
    const [standardPaymentCompanyNames, setStandardPaymentCompanyNames] = useState([]);
    const [standardPaymentCompanySearch, setStandardPaymentCompanySearch] = useState('');
    const [standardPaymentLoading, setStandardPaymentLoading] = useState(false);
    const [standardPaymentError, setStandardPaymentError] = useState(null);
    const [standardPaymentSuccess, setStandardPaymentSuccess] = useState(null);

    // Onglet relances e-mail bulk remboursement (bank details)
    const [bulkRemindersFile, setBulkRemindersFile] = useState(null);
    const [bulkRemindersLoading, setBulkRemindersLoading] = useState(false);
    const [bulkRemindersError, setBulkRemindersError] = useState(null);
    const [bulkRemindersResult, setBulkRemindersResult] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = forecastPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [forecastPartners]);

    const genericPaymentFilteredPartnerOptions = useMemo(() => {
        const query = genericPaymentCompanySearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, genericPaymentCompanySearch]);

    const standardPaymentFilteredPartnerOptions = useMemo(() => {
        const query = standardPaymentCompanySearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, standardPaymentCompanySearch]);

    // Initialiser la date du jour pour les formulaires de génération
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        if (!claimDate) setClaimDate(today);
        if (!claimDateByCodes) setClaimDateByCodes(today);
    }, []);

    const handleDetails = (item) => { setSelectedItem(item); setShowDetailModal(true); };

    const handleEdit = (item) => { setSelectedItem(item); setShowEditModal(true); };

    const handleDelete = (item) => { setSelectedItem(item); setShowDeleteModal(true); };

    const confirmDelete = () => {
        if (selectedItem) {
            // Note: La suppression devrait être gérée par l'API
            // Pour l'instant, on recharge simplement la liste
            setShowDeleteModal(false);
            setSelectedItem(null);
            loadClaims(currentPage);
        }
    };

    // Sub-claim handlers
    const handleClaimDetails = (policyId, claim) => {
        navigate(`/admin/trips/claims/${claim?.claim_id || 'detail'}`, {
            state: { claim, policyId }
        });
    };

    const handleClaimEdit = (policyId, claim) => {
        setSelectedPolicyIdForClaim(policyId);
        setSelectedClaim(claim);
        setShowClaimEditModal(true);
    };

    const handleClaimDelete = (policyId, claim) => {
        setSelectedPolicyIdForClaim(policyId);
        setSelectedClaim(claim);
        setShowClaimDeleteModal(true);
    };

    const confirmClaimDelete = async () => {
        if (!selectedClaim) return;
        try {
            setDeleteError(null);
            setDeleteSuccess(null);
            await deleteClaimById(selectedClaim.claim_id);
            setDeleteSuccess('Sinistre supprimé avec succès !');
            setShowClaimDeleteModal(false);
            setSelectedClaim(null);
            setSelectedPolicyIdForClaim(null);
            // Recharger la liste après un court délai pour voir le message
            setTimeout(() => {
                loadClaims(currentPage);
                setDeleteSuccess(null);
            }, 1500);
        } catch (e) {
            const errorMessage = e?.response?.data?.detail || e?.message || 'Erreur lors de la suppression du sinistre';
            setDeleteError(errorMessage);
            console.error('Erreur lors de la suppression du sinistre:', e);
        }
    };

    // Même contrat que l’API pagination trips/claims : activity_end_date_* en DD/MM/YYYY (inputs date = YYYY-MM-DD)
    const convertDateToDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr;
        if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        }
        return dateStr;
    };

    const appendClaimsFiltersToParams = (params, activeFilters) => {
        Object.keys(activeFilters).forEach((key) => {
            const value = activeFilters[key];
            if (key === 'trip_modified') {
                if (value === 'true' || value === 'false') {
                    params[key] = value;
                }
                return;
            }
            if (value == null || String(value).trim() === '') return;
            const trimmed = String(value).trim();
            if (DDMMYYYY_CLAIMS_DATE_FILTER_KEYS.has(key)) {
                params[key] = convertDateToDDMMYYYY(trimmed);
            } else {
                params[key] = trimmed;
            }
        });
    };

    // Fonctions pour l'onglet Trigger verification
    const loadCheckClaims = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : checkClaimsAppliedFilters;
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                if (key === 'activity_end_date_from' || key === 'activity_end_date_to') {
                    params[key] = convertDateToDDMMYYYY(activeFilters[key].trim());
                } else {
                    params[key] = activeFilters[key].trim();
                }
            }
        });

        dispatch(fetchTripsCheckClaimsRequest(params));
    }, [itemsPerPage, checkClaimsAppliedFilters, dispatch]);

    const handleCheckClaimsFilterChange = (e) => {
        const { name, value } = e.target;
        setCheckClaimsFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckClaimsApplyFilters = () => {
        setCheckClaimsAppliedFilters({ ...checkClaimsFilters });
        setCheckClaimsPage(1);
        loadCheckClaims(1, checkClaimsFilters);
    };

    const handleCheckClaimsClearFilters = () => {
        const emptyFilters = {
            reservation_number: '',
            policy_code: '',
            client_email: '',
            activity_end_date_from: '',
            activity_end_date_to: '',
            claim_code: '',
            date_of_claim: '',
            company_name: '',
            reference_name: '',
        };
        setCheckClaimsFilters(emptyFilters);
        setCheckClaimsAppliedFilters(emptyFilters);
        setCheckClaimsPage(1);
        setTimeout(() => {
            loadCheckClaims(1, emptyFilters);
        }, 100);
    };

    const handleBulkRemindersEmail = async (e) => {
        e.preventDefault();
        if (!bulkRemindersFile) {
            setBulkRemindersError('Veuillez sélectionner un fichier.');
            return;
        }
        try {
            setBulkRemindersLoading(true);
            setBulkRemindersError(null);
            setBulkRemindersResult(null);
            const result = await postTripsClientsBankDetailsBulkRemindersEmail(bulkRemindersFile);
            setBulkRemindersResult(result);
        } catch (error) {
            setBulkRemindersError(handleApiError(error, 'Erreur lors de l\'envoi des relances e-mail'));
        } finally {
            setBulkRemindersLoading(false);
        }
    };

    useEffect(() => {
        const loadForecastPartners = async () => {
            try {
                setForecastPartnersLoading(true);
                setForecastPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setForecastPartners(partnersArray);
            } catch (error) {
                setForecastPartners([]);
                setForecastPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setForecastPartnersLoading(false);
            }
        };

        if (isAuthenticated) {
            loadForecastPartners();
        }
    }, [isAuthenticated]);

    // Fonctions pour l'onglet Générer le lien RIB
    const handleRibSubmit = async (e) => {
        e.preventDefault();
        if (!policyCode.trim()) {
            setRibError('Le code de police est requis');
            return;
        }

        try {
            setRibLoading(true);
            setRibError(null);
            setRibUrl(null);
            setRibInfo(null);
            const data = await generateRibFormUrl(policyCode.trim());
            setRibUrl(data?.url || data?.rib_form_url || null);
            setRibInfo(data || null);
        } catch (err) {
            setRibError(handleApiError(err, 'Erreur lors de la génération du lien RIB'));
        } finally {
            setRibLoading(false);
        }
    };

    const handleRibCopy = async () => {
        if (ribUrl) {
            try {
                await navigator.clipboard.writeText(ribUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                setRibError('Impossible de copier le lien dans le presse-papiers');
            }
        }
    };

    const handleRibOpenLink = () => {
        if (ribUrl) {
            window.open(ribUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleRibReset = () => {
        setPolicyCode('');
        setRibUrl(null);
        setRibInfo(null);
        setRibError(null);
    };

    // Charger les check claims quand l'onglet est actif
    useEffect(() => {
        if (isAuthenticated && activeTab === 'trigger-verification') {
            loadCheckClaims(checkClaimsPage);
        }
    }, [isAuthenticated, activeTab, checkClaimsPage, loadCheckClaims]);

    // Extraire les données des check claims
    const checkClaimsData = checkClaimsState?.data?.data || checkClaimsState?.data || [];
    const checkClaimsArray = Array.isArray(checkClaimsData) ? checkClaimsData : [];
    const checkClaimsMeta = checkClaimsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: checkClaimsPage,
        total_pages: 0,
    };
    const checkClaimsTotalPages = checkClaimsMeta.total_pages || Math.max(1, Math.ceil(checkClaimsMeta.total / itemsPerPage));
    const checkClaimsTotalItems = checkClaimsMeta.total || 0;
    const checkClaimsIndexOfFirstItem = checkClaimsMeta.skip || 0;
    const checkClaimsIndexOfLastItem = Math.min(checkClaimsIndexOfFirstItem + checkClaimsArray.length, checkClaimsTotalItems);

    // Handlers pour Generate Bank Transfer File
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        if (!claimDate) setClaimDate(today);
        if (!claimDateByCodes) setClaimDateByCodes(today);
    }, []);

    const handleGenerateByDate = () => {
        const params = {};
        if (claimDate) {
            params.claim_date = claimDate;
        }
        dispatch(generateBulkTransfersCsvRequest(params));
    };

    const handleGenerateByPolicyCodes = () => {
        const codes = policyCodes
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0);

        if (codes.length === 0) {
            alert('Veuillez entrer au moins un code de police');
            return;
        }

        const params = {
            policy_codes: codes,
        };
        if (claimDateByCodes) {
            params.claim_date = claimDateByCodes;
        }
        dispatch(generateBulkTransfersCsvByPolicyCodesRequest(params));
    };

    const handleGenerateEndOfStay = () => {
        const params = {};
        if (tripEndDateFrom) {
            params.trip_end_date_from = tripEndDateFrom;
        }
        if (tripEndDateTo) {
            params.trip_end_date_to = tripEndDateTo;
        }
        dispatch(generateBulkTransfersCsvEndOfStayRequest(params));
    };

    const handleGenerateEndOfStayByPolicyCodes = () => {
        const codes = policyCodesEndOfStay
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0);

        if (codes.length === 0) {
            alert('Veuillez entrer au moins un code de police');
            return;
        }

        const params = {
            policy_codes: codes,
        };
        dispatch(generateBulkTransfersCsvEndOfStayByPolicyCodesRequest(params));
    };

    const buildClaimsImportSuccessMessage = (result) => {
        if (!result) {
            return 'Succès :\n0 claims updated successfully\n\nRésumé :\n- Claims mis à jour : 0\n- Échecs : 0';
        }

        const updatedCount = result.summary?.updated || result.updated_claims?.length || result.successful_updates || 0;
        const failedCount = result.summary?.failed || result.failed_rows?.length || result.failed_updates || 0;
        let successMessage = `Succès :\n${updatedCount} claims updated successfully\n\nRésumé :\n- Claims mis à jour : ${updatedCount}\n- Échecs : ${failedCount}`;

        if (result.failed_rows && result.failed_rows.length > 0) {
            successMessage += '\n\nLignes échouées :';
            result.failed_rows.forEach((failedRow, index) => {
                successMessage += `\n${index + 1}. Ligne ${failedRow.row} (Policy: ${failedRow.policy_code || 'N/A'}) : ${failedRow.error || 'Erreur inconnue'}`;
            });
        }

        if (result.results?.errors && Array.isArray(result.results.errors) && result.results.errors.length > 0) {
            successMessage += '\n\nLignes échouées :';
            result.results.errors.forEach((error, index) => {
                const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
                const errorMsg = error.error || error.message || 'Erreur inconnue';
                successMessage += `\n${index + 1}. Réservation ${reservationNumber}: ${errorMsg || 'Erreur non spécifiée'}`;
            });
        }

        return successMessage;
    };

    const extractImportErrorMessage = (error) => {
        let errorMessage = 'Erreur lors de l\'import. Veuillez vérifier votre fichier et réessayer.';

        if (error.response) {
            const responseData = error.response.data;
            if (responseData?.detail) {
                if (typeof responseData.detail === 'string') {
                    errorMessage = responseData.detail;
                } else if (responseData.detail?.message) {
                    errorMessage = responseData.detail.message;
                } else {
                    errorMessage = JSON.stringify(responseData.detail);
                }
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else if (responseData?.error) {
                errorMessage = typeof responseData.error === 'string'
                    ? responseData.error
                    : responseData.error.message || JSON.stringify(responseData.error);
            }
        } else if (error.message) {
            errorMessage = error.message;
        }

        return errorMessage;
    };

    // Handler pour Update Claim Status
    const handleClaimsManagementImport = async () => {
        if (!importFile) {
            setImportError('Veuillez sélectionner un fichier Excel');
            return;
        }
        if (!claimsManagementImportType || claimsManagementImportType.trim() === '') {
            setImportError('Veuillez sélectionner un type d\'import');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            let importFunction;
            let typeName;

            if (claimsManagementImportType === 'olela') {
                importFunction = importClaimsBulkUpdatesOlela;
                typeName = 'Olela';
            } else if (claimsManagementImportType === 'flower') {
                importFunction = importClaimsBulkUpdatesFlower;
                typeName = 'Flower';
            } else if (claimsManagementImportType === 'b2c') {
                importFunction = importClaimsBulkUpdatesB2C;
                typeName = 'B2C';
            } else {
                setImportError(`Type d'import non reconnu: ${claimsManagementImportType}`);
                setImportLoading(false);
                return;
            }

            if (!importFunction) {
                setImportError('Fonction d\'import non disponible');
                setImportLoading(false);
                return;
            }

            const result = await importFunction(importFile);

            setImportSuccess(buildClaimsImportSuccessMessage(result));
            setImportFile(null);
            setClaimsManagementImportType('');
        } catch (error) {
            console.error('Erreur lors de l\'import:', error);
            setImportError(extractImportErrorMessage(error));
        } finally {
            setImportLoading(false);
        }
    };

    const handleGenericPaymentImport = async () => {
        if (!genericPaymentFile) {
            setGenericPaymentError('Veuillez sélectionner un fichier Excel');
            return;
        }

        setGenericPaymentLoading(true);
        setGenericPaymentError(null);
        setGenericPaymentSuccess(null);

        try {
            const result = await importClaimsBulkUpdatesGeneric(genericPaymentFile, {
                company_name: genericPaymentCompanyNames,
            });
            setGenericPaymentSuccess(buildClaimsImportSuccessMessage(result));
            setGenericPaymentFile(null);
        } catch (error) {
            console.error('Erreur lors de l\'import paiements générique:', error);
            setGenericPaymentError(extractImportErrorMessage(error));
        } finally {
            setGenericPaymentLoading(false);
        }
    };

    const handleStandardPaymentImport = async () => {
        if (!standardPaymentFile) {
            setStandardPaymentError('Veuillez sélectionner un fichier Excel');
            return;
        }

        setStandardPaymentLoading(true);
        setStandardPaymentError(null);
        setStandardPaymentSuccess(null);

        try {
            const result = await importClaimsBulkUpdatesStandard(standardPaymentFile, {
                company_name: standardPaymentCompanyNames,
            });
            setStandardPaymentSuccess(buildClaimsImportSuccessMessage(result));
            setStandardPaymentFile(null);
        } catch (error) {
            console.error('Erreur lors de l\'import paiements Standard:', error);
            setStandardPaymentError(extractImportErrorMessage(error));
        } finally {
            setStandardPaymentLoading(false);
        }
    };

    // Charger les sinistres avec pagination et filtres
    const loadClaims = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;
        appendClaimsFiltersToParams(params, activeFilters);

        dispatch(fetchTripsClaimsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadClaims(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        loadClaims(currentPage);
    }, [currentPage, loadClaims]);

    // Extraire les données et métadonnées de la réponse API
    const claimsData = claimsState?.data?.data || claimsState?.data?.policies_with_claims || claimsState?.data || [];
    const claimsArray = Array.isArray(claimsData) ? claimsData : [];
    const meta = claimsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + claimsArray.length, totalItems);

    const handleRefresh = () => {
        loadClaims(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadClaims(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_EXPORT_CLAIMS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadClaims(1, emptyFilters);
        }, 100);
    };

    const buildTripsClaimsExportParams = (activeFilters) => {
        const params = {};
        const {
            activity_end_date_to: _activityEndDateTo,
            new_trip_start_date_to: _newTripStartDateTo,
            new_trip_end_date_to: _newTripEndDateTo,
            ...exportFilters
        } = activeFilters || {};
        appendClaimsFiltersToParams(params, exportFilters);
        return params;
    };

    const handleExportClaimsFilterChange = (e) => {
        const { name, value } = e.target;
        setExportClaimsFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleExportClaimsCopyFromClaimsTab = () => {
        const copied = {
            ...EMPTY_EXPORT_CLAIMS_FILTERS,
            ...appliedFilters,
            activity_end_date_to: '',
            new_trip_start_date_to: '',
            new_trip_end_date_to: '',
        };
        setExportClaimsFilters(copied);
        setExportCompanyPartnerSearch(copied.company_name || '');
    };

    const handleExportClaimsClearFilters = () => {
        setExportClaimsFilters({ ...EMPTY_EXPORT_CLAIMS_FILTERS });
        setExportCompanyPartnerSearch('');
    };

    const handleExportClaimsExcel = async () => {
        setExportClaimsLoading(true);
        setExportClaimsError(null);
        try {
            const companyName = (exportClaimsFilters.company_name || exportCompanyPartnerSearch).trim();
            const activeExportFilters = { ...exportClaimsFilters, company_name: companyName };
            setExportClaimsFilters(activeExportFilters);
            setExportCompanyPartnerSearch(companyName);
            const params = buildTripsClaimsExportParams(activeExportFilters);
            const data = await getTripsClaimsFullList(params);
            const rows = flattenPoliciesWithClaimsForExcel(data);
            if (!rows.length) {
                setExportClaimsError('Aucune ligne à exporter pour ces filtres.');
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(rows, { header: TRIPS_CLAIMS_EXPORT_COLUMNS });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
            XLSX.writeFile(workbook, `trips_claims_export_${stamp}.xlsx`);
        } catch (err) {
            setExportClaimsError(handleApiError(err, 'Erreur lors de l\'export des sinistres'));
        } finally {
            setExportClaimsLoading(false);
        }
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les sinistres de l'API (pas de filtrage côté client)
    const currentClaims = claimsArray;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try { const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj); return val ?? fallback; } catch { return fallback; }
    };

    const toggleExpand = (item) => {
        const id = getDeep(item, ['policy_id'], getDeep(item, ['policy_info', 'policy_id']));
        setExpandedPolicies(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Auto-déplier la première police pour montrer ses claims
    useEffect(() => {
        if (currentClaims && currentClaims.length > 0) {
            const first = currentClaims[0];
            const id = getDeep(first, ['policy_id'], getDeep(first, ['policy_info', 'policy_id']));
            setExpandedPolicies(prev => ({ ...prev, [id]: true }));
        }
    }, [currentClaims]);

    return (
        <div className="dashboard-page admin-trips-claims-page">
            <Card>
                <div>
                    <h2 className="page-title">
                        <FiAlertTriangle className="me-2" />
                        Trip - Gestion Sinistres
                    </h2>
                    <p className="text-muted mb-0">Sinistres liés aux séjours</p>
                </div>
                <Card.Body>
                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'claims')}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="claims">
                                    <FiAlertTriangle className="me-2" />
                                    Claims
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="export-claims">
                                    <FiDownload className="me-2" />
                                    Export Claims
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="weather-details">
                                    <FiCloud className="me-2" />
                                    Weather Details
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="trigger-verification">
                                    <FiCheckCircle className="me-2" />
                                    Trigger verification
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="rib-form">
                                    <FiLink className="me-2" />
                                    Générer le lien RIB
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="bulk-transfers">
                                    <FiDownload className="me-2" />
                                    Generate Bank Transfer File
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="standard-bulk-transfers">
                                    <FiDownload className="me-2" />
                                    Standard Generate Bank Transfer File
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="claims-management">
                                    <FiSettings className="me-2" />
                                    Update Claim Status
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="generic-payment-import">
                                    <FiUpload className="me-2" />
                                    Import claims status paid
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="standard-payment-import">
                                    <FiUpload className="me-2" />
                                    Import claims status paid (Standard)
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="bulk-reminders-email">
                                    <FiUpload className="me-2" />
                                    Relances remboursement (fichier)
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="claims">

                                {claimsState.error && (
                                    <Alert variant="danger" className="mt-3">
                                        {claimsState.error}
                                    </Alert>
                                )}

                                {deleteSuccess && (
                                    <Alert variant="success" className="mt-3" onClose={() => setDeleteSuccess(null)} dismissible>
                                        {deleteSuccess}
                                    </Alert>
                                )}

                                {deleteError && (
                                    <Alert variant="danger" className="mt-3" onClose={() => setDeleteError(null)} dismissible>
                                        {deleteError}
                                    </Alert>
                                )}

                                {/* Filtres avancés */}
                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Filtres de recherche</h6>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                                Réinitialiser
                                            </Button>
                                            <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                                Appliquer les filtres
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code police</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="policy_code"
                                                        value={filters.policy_code}
                                                        onChange={handleFilterChange}
                                                        onKeyPress={handleKeyPress}
                                                        placeholder="Ex: 301826051"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Numéro de réservation</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reservation_number"
                                                        value={filters.reservation_number}
                                                        onChange={handleFilterChange}
                                                        onKeyPress={handleKeyPress}
                                                        placeholder="Numéro de réservation"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Email client</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="client_email"
                                                        value={filters.client_email}
                                                        onChange={handleFilterChange}
                                                        onKeyPress={handleKeyPress}
                                                        placeholder="Email du client"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date fin activité (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="activity_end_date_from"
                                                        value={filters.activity_end_date_from}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date fin activité (à)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="activity_end_date_to"
                                                        value={filters.activity_end_date_to}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code sinistre</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="claim_code"
                                                        value={filters.claim_code}
                                                        onChange={handleFilterChange}
                                                        onKeyPress={handleKeyPress}
                                                        placeholder="Code du sinistre"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date du sinistre</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="date_of_claim"
                                                        value={filters.date_of_claim}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Statut sinistre</Form.Label>
                                                    <Form.Select
                                                        name="claim_status"
                                                        value={filters.claim_status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">Tous</option>
                                                        {TRIP_CLAIM_STATUS_OPTIONS.map((status) => (
                                                            <option key={status} value={status}>{status}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label>Nom de société</Form.Label>
                                                <PartnerCompanyAutocomplete
                                                    value={filters.company_name}
                                                    onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                                    onInputChange={setCompanyPartnerSearch}
                                                    options={partnerCompanyOptions}
                                                    loading={forecastPartnersLoading}
                                                    error={forecastPartnersError}
                                                    onKeyDown={handleKeyPress}
                                                    placeholder="Rechercher un partenaire…"
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Dates séjour modifiées</Form.Label>
                                                    <Form.Select
                                                        name="trip_modified"
                                                        value={filters.trip_modified}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="true">Oui</option>
                                                        <option value="false">Non</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouveau début séjour (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_start_date_from"
                                                        value={filters.new_trip_start_date_from}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouveau début séjour (à)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_start_date_to"
                                                        value={filters.new_trip_start_date_to}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_end_date_from"
                                                        value={filters.new_trip_end_date_from}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouvelle fin séjour (à)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_end_date_to"
                                                        value={filters.new_trip_end_date_to}
                                                        onChange={handleFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-end gap-2 mb-3">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleRefresh}
                                        disabled={claimsState.loading}
                                        className="d-flex align-items-center"
                                    >
                                        <FiRefreshCw className={`me-2 ${claimsState.loading ? 'spinning' : ''}`} />
                                        Actualiser
                                    </Button>
                                </div>

                                {claimsState.loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Chargement des sinistres (trips)...</p>
                                    </div>
                                ) : claimsState.error ? (
                                    <div className="text-center py-5">
                                        <Alert variant="danger">{claimsState.error}</Alert>
                                    </div>
                                ) : currentClaims.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FiAlertTriangle size={48} className="text-muted mb-3" />
                                        <p className="text-muted">Aucun sinistre disponible</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <div className="table-scroll">
                                            <Table hover striped className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>Code police</th>
                                                        <th>Langue préférée</th>
                                                        <th>Total</th>
                                                        <th>Nouv. total</th>
                                                        <th>Voucher</th>
                                                        <th>Nouv. voucher</th>
                                                        <th>Cash</th>
                                                        <th>Nouv. cash</th>
                                                        <th>Nouveau début</th>
                                                        <th>Nouvelle fin</th>
                                                        <th>Séjour modifié</th>
                                                        <th>Statut police</th>
                                                        <th># Sinistres</th>
                                                        <th>Dernière modif.</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.isArray(currentClaims) && currentClaims.length > 0 ? (
                                                        currentClaims.map((item, idx) => {
                                                            const policyId = getDeep(item, ['policy_id'], getDeep(item, ['policy_info', 'policy_id'], idx));
                                                            const claims = getDeep(item, ['claims'], []);
                                                            const tripDateModified = resolveTripDateModified(item);
                                                            const newTripStartDate = resolveNewTripStartDate(item);
                                                            const newTripEndDate = resolveNewTripEndDate(item);
                                                            const isOpen = !!expandedPolicies[policyId];
                                                            return (
                                                                <>
                                                                    <tr
                                                                        key={policyId}
                                                                        onClick={() => toggleExpand(item)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['policy_info', 'policy_code'])}</td>
                                                                        <td>{getDeep(item, ['policy_info', 'preferred_language'], '—')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['Total_claim'], 'amount')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['New_Total_claim', 'new_Total_claim', 'new_total_claim'], 'newAmount')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['Total_claim_voucher'], 'voucher')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['New_Total_claim_voucher', 'new_Total_claim_voucher', 'new_total_claim_voucher'], 'newVoucher')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['Total_claim_cash'], 'cash')}</td>
                                                                        <td>{resolvePolicyAmountTotal(item, ['New_Total_claim_cash', 'new_Total_claim_cash', 'new_total_claim_cash'], 'newCash')}</td>
                                                                        <td>{newTripStartDate || '—'}</td>
                                                                        <td>{newTripEndDate || '—'}</td>
                                                                        <td>{renderTripModifiedBadge(tripDateModified)}</td>
                                                                        <td>{getDeep(item, ['policy_info', 'status'])}</td>
                                                                        <td>{Array.isArray(claims) ? claims.length : 0}</td>
                                                                        <td>{getDeep(item, ['claims', 0, 'claim_last_modified_at'], '—')}</td>

                                                                    </tr>
                                                                    {isOpen && Array.isArray(claims) && claims.length > 0 && (
                                                                        <tr>
                                                                            <td colSpan={TRIPS_CLAIMS_TABLE_COLSPAN}>
                                                                                <div className="table-responsive">
                                                                                    <Table size="sm" bordered className="mb-0">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th><span className="me-1">€</span> Montant</th>
                                                                                                <th><span className="me-1">€</span> Nouv. montant</th>
                                                                                                <th><span className="me-1">€</span> Cash</th>
                                                                                                <th><span className="me-1">€</span> Nouv. cash</th>
                                                                                                <th><FiGift className="me-1" /> Voucher</th>
                                                                                                <th><FiGift className="me-1" /> Nouv. voucher</th>
                                                                                                <th><FiCheckCircle className="me-1" /> Statut</th>
                                                                                                <th><FiCalendar className="me-1" /> Date sinistre</th>
                                                                                                <th><FiCalendar className="me-1" /> Date paiement</th>
                                                                                                <th className="text-end">Actions</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {claims.map((c, i) => {
                                                                                                const claimActivity = resolveClaimActivityInfo(c, item);
                                                                                                const amounts = getClaimTableAmounts(c);
                                                                                                return (
                                                                                                    <tr key={c.claim_id || i}>
                                                                                                        <td>{formatClaimAmount(amounts.amount)}</td>
                                                                                                        <td>{formatClaimAmount(amounts.newAmount)}</td>
                                                                                                        <td><span className="me-1 text-success">€</span>{formatClaimAmount(amounts.cash)}</td>
                                                                                                        <td><span className="me-1 text-success">€</span>{formatClaimAmount(amounts.newCash)}</td>
                                                                                                        <td><FiGift className="me-1 text-primary" />{formatClaimAmount(amounts.voucher)}</td>
                                                                                                        <td><FiGift className="me-1 text-primary" />{formatClaimAmount(amounts.newVoucher)}</td>
                                                                                                        <td><FiCheckCircle className="me-1 text-secondary" />{getDeep(c, ['claim_status'], '—')}</td>
                                                                                                        <td>
                                                                                                            <FiCalendar className="me-1 text-muted" />
                                                                                                            {getDeep(c, ['date_of_claim'], '—')}
                                                                                                            {c?.forecast_info?.trip_day && (
                                                                                                                <span className="ms-2 text-muted small">
                                                                                                                    (jour: {c.forecast_info.trip_day})
                                                                                                                </span>
                                                                                                            )}
                                                                                                            {(isTruthyFlag(claimActivity.trip_modified) || tripDateModified) && (
                                                                                                                <Badge bg="warning" className="ms-2">Modifié</Badge>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            <FiCalendar className="me-1 text-muted" />
                                                                                                            {getDeep(c, ['payment_date'], '—')}
                                                                                                        </td>
                                                                                                        <td className="text-end">
                                                                                                            <Button variant="outline-info" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleClaimDetails(policyId, c); }} title="Détails" aria-label="Détails">
                                                                                                                <FiEye />
                                                                                                            </Button>
                                                                                                            <Button variant="outline-primary" size="sm" className="me-2" onClick={(e) => { e.stopPropagation(); handleClaimEdit(policyId, c); }} title="Modifier" aria-label="Modifier">
                                                                                                                <FiEdit />
                                                                                                            </Button>
                                                                                                            <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); handleClaimDelete(policyId, c); }} title="Supprimer" aria-label="Supprimer">
                                                                                                                <FiTrash2 />
                                                                                                            </Button>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </Table>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={TRIPS_CLAIMS_TABLE_COLSPAN} className="text-center">
                                                                Aucun sinistre trouvé
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                )}

                                {/* Pagination */}
                                {totalItems > 0 && totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <div className="text-muted">
                                            Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} sinistre{totalItems > 1 ? 's' : ''}
                                        </div>
                                        <Pagination className="mb-0">
                                            <Pagination.First
                                                onClick={() => {
                                                    setCurrentPage(1);
                                                    loadClaims(1);
                                                }}
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev
                                                onClick={() => {
                                                    const prevPage = currentPage - 1;
                                                    setCurrentPage(prevPage);
                                                    loadClaims(prevPage);
                                                }}
                                                disabled={currentPage === 1}
                                            />
                                            {(() => {
                                                const { startPage, endPage } = getPaginationRange();
                                                const pages = [];

                                                // Show first page if not in range
                                                if (startPage > 1) {
                                                    pages.push(
                                                        <Pagination.Item key={1} onClick={() => {
                                                            setCurrentPage(1);
                                                            loadClaims(1);
                                                        }}>
                                                            1
                                                        </Pagination.Item>
                                                    );
                                                    if (startPage > 2) {
                                                        pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                                    }
                                                }

                                                // Show pages in range
                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(
                                                        <Pagination.Item
                                                            key={i}
                                                            active={i === currentPage}
                                                            onClick={() => {
                                                                setCurrentPage(i);
                                                                loadClaims(i);
                                                            }}
                                                        >
                                                            {i}
                                                        </Pagination.Item>
                                                    );
                                                }

                                                // Show last page if not in range
                                                if (endPage < totalPages) {
                                                    if (endPage < totalPages - 1) {
                                                        pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                                    }
                                                    pages.push(
                                                        <Pagination.Item key={totalPages} onClick={() => {
                                                            setCurrentPage(totalPages);
                                                            loadClaims(totalPages);
                                                        }}>
                                                            {totalPages}
                                                        </Pagination.Item>
                                                    );
                                                }

                                                return pages;
                                            })()}
                                            <Pagination.Next
                                                onClick={() => {
                                                    const nextPage = currentPage + 1;
                                                    setCurrentPage(nextPage);
                                                    loadClaims(nextPage);
                                                }}
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last
                                                onClick={() => {
                                                    setCurrentPage(totalPages);
                                                    loadClaims(totalPages);
                                                }}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                                {/* Modals */}
                                <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                                    <Modal.Header closeButton>
                                        <Modal.Title>Détails des sinistres</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(selectedItem, null, 2)}</pre>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Fermer</Button>
                                    </Modal.Footer>
                                </Modal>

                                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Modifier (à implémenter)</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        Cette fonctionnalité sera branchée à l'API d'édition lorsque disponible.
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Fermer</Button>
                                    </Modal.Footer>
                                </Modal>

                                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Confirmer la suppression</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        Confirmer la suppression (UI) ?
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
                                        <Button variant="danger" onClick={confirmDelete}>Supprimer</Button>
                                    </Modal.Footer>
                                </Modal>

                                {/* Sub-claim Modals */}
                                <Modal show={showClaimDetailModal} onHide={() => setShowClaimDetailModal(false)} size="lg">
                                    <Modal.Header closeButton>
                                        <Modal.Title>Détail du sinistre</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(selectedClaim, null, 2)}</pre>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowClaimDetailModal(false)}>Fermer</Button>
                                    </Modal.Footer>
                                </Modal>

                                <Modal show={showClaimEditModal} onHide={() => setShowClaimEditModal(false)}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Modifier le sinistre (à implémenter)</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        Cette fonctionnalité sera branchée à l'API lorsque disponible.
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowClaimEditModal(false)}>Fermer</Button>
                                    </Modal.Footer>
                                </Modal>

                                <Modal show={showClaimDeleteModal} onHide={() => {
                                    setShowClaimDeleteModal(false);
                                    setDeleteError(null);
                                    setSelectedClaim(null);
                                    setSelectedPolicyIdForClaim(null);
                                }}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>Confirmer la suppression du sinistre</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {deleteError && (
                                            <Alert variant="danger" className="mb-3">
                                                {deleteError}
                                            </Alert>
                                        )}
                                        <p>Êtes-vous sûr de vouloir supprimer ce sinistre ?</p>
                                        {selectedClaim && (
                                            <div className="mt-2">
                                                <strong>Code sinistre:</strong> {getDeep(selectedClaim, ['claim_code'], 'N/A')}<br />
                                                <strong>Montant:</strong> {formatClaimAmount(getClaimTableAmounts(selectedClaim).amount)}
                                            </div>
                                        )}
                                        <p className="text-danger mt-2"><small>Cette action est irréversible.</small></p>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => {
                                            setShowClaimDeleteModal(false);
                                            setDeleteError(null);
                                            setSelectedClaim(null);
                                            setSelectedPolicyIdForClaim(null);
                                        }}>Annuler</Button>
                                        <Button variant="danger" onClick={confirmClaimDelete} disabled={deleteSuccess}>
                                            {deleteSuccess ? 'Suppression en cours...' : 'Supprimer'}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </Tab.Pane>

                            <Tab.Pane eventKey="export-claims">
                                {exportClaimsError && (
                                    <Alert variant="danger" className="mb-3" onClose={() => setExportClaimsError(null)} dismissible>
                                        {exportClaimsError}
                                    </Alert>
                                )}
                                <Alert variant="info" className="mb-3">
                                    Export Excel à partir de <code>GET api/trips/claims</code> (résultat complet sans pagination).
                                    Les filtres sont optionnels ; pour les dates de fin d&apos;activité, le même format qu&apos;à l&apos;onglet Claims est envoyé (DD/MM/YYYY).
                                    Les volumes importants peuvent prendre jusqu&apos;à 2 minutes.
                                </Alert>
                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h6 className="mb-0">Filtres d&apos;export (tous optionnels)</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            <Button variant="outline-primary" size="sm" onClick={handleExportClaimsCopyFromClaimsTab} type="button">
                                                <FiCopy className="me-1" />
                                                Reprendre les filtres de l&apos;onglet Claims
                                            </Button>
                                            <Button variant="outline-secondary" size="sm" onClick={handleExportClaimsClearFilters} type="button">
                                                Réinitialiser
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code police</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="policy_code"
                                                        value={exportClaimsFilters.policy_code}
                                                        onChange={handleExportClaimsFilterChange}
                                                        placeholder="Ex: 301826051"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Numéro de réservation</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reservation_number"
                                                        value={exportClaimsFilters.reservation_number}
                                                        onChange={handleExportClaimsFilterChange}
                                                        placeholder="Numéro de réservation"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Email client</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="client_email"
                                                        value={exportClaimsFilters.client_email}
                                                        onChange={handleExportClaimsFilterChange}
                                                        placeholder="Email du client"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date fin activité (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="activity_end_date_from"
                                                        value={exportClaimsFilters.activity_end_date_from}
                                                        onChange={handleExportClaimsFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code sinistre</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="claim_code"
                                                        value={exportClaimsFilters.claim_code}
                                                        onChange={handleExportClaimsFilterChange}
                                                        placeholder="Code du sinistre"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Date du sinistre</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="date_of_claim"
                                                        value={exportClaimsFilters.date_of_claim}
                                                        onChange={handleExportClaimsFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Statut sinistre</Form.Label>
                                                    <Form.Select
                                                        name="claim_status"
                                                        value={exportClaimsFilters.claim_status}
                                                        onChange={handleExportClaimsFilterChange}
                                                    >
                                                        <option value="">Tous</option>
                                                        {TRIP_CLAIM_STATUS_OPTIONS.map((status) => (
                                                            <option key={status} value={status}>{status}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label>Nom de société</Form.Label>
                                                <PartnerCompanyAutocomplete
                                                    value={exportClaimsFilters.company_name}
                                                    onChange={(companyName) => setExportClaimsFilters((prev) => ({ ...prev, company_name: companyName }))}
                                                    onInputChange={setExportCompanyPartnerSearch}
                                                    options={partnerCompanyOptions}
                                                    loading={forecastPartnersLoading}
                                                    error={forecastPartnersError}
                                                    placeholder="Rechercher un partenaire…"
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Dates séjour modifiées</Form.Label>
                                                    <Form.Select
                                                        name="trip_modified"
                                                        value={exportClaimsFilters.trip_modified}
                                                        onChange={handleExportClaimsFilterChange}
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="true">Oui</option>
                                                        <option value="false">Non</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouveau début séjour (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_start_date_from"
                                                        value={exportClaimsFilters.new_trip_start_date_from}
                                                        onChange={handleExportClaimsFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="new_trip_end_date_from"
                                                        value={exportClaimsFilters.new_trip_end_date_from}
                                                        onChange={handleExportClaimsFilterChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    <Button
                                        variant="primary"
                                        onClick={handleExportClaimsExcel}
                                        disabled={exportClaimsLoading}
                                        className="d-flex align-items-center"
                                    >
                                        {exportClaimsLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Génération…
                                            </>
                                        ) : (
                                            <>
                                                <FiDownload className="me-2" />
                                                Télécharger Excel
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Tab.Pane>

                            <Tab.Pane eventKey="weather-details">
                                <TripWeatherDetailsPanel
                                    embedded
                                    fetchPayload={fetchClaimsWeatherPayload}
                                    apiEndpointLabel="GET /api/trips/claims"
                                    title="Weather Details"
                                    subtitle="Observations horaires (précipitations & températures) issues des sinistres. Filtres code police, réservation et email optionnels."
                                    initialFilters={{
                                        policy_code: appliedFilters?.policy_code || '',
                                        reservation_number: appliedFilters?.reservation_number || '',
                                        client_email: appliedFilters?.client_email || '',
                                    }}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="trigger-verification">
                                {checkClaimsState.error && (
                                    <Alert variant="danger" className="mb-3">
                                        {checkClaimsState.error}
                                    </Alert>
                                )}

                                {/* Filtres */}
                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Filtres de recherche</h6>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={handleCheckClaimsClearFilters}>
                                                Réinitialiser
                                            </Button>
                                            <Button variant="primary" size="sm" onClick={handleCheckClaimsApplyFilters}>
                                                Appliquer les filtres
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code police</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="policy_code"
                                                        value={checkClaimsFilters.policy_code}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Ex: 301826051"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Numéro de réservation</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reservation_number"
                                                        value={checkClaimsFilters.reservation_number}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Numéro de réservation"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Email client</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="client_email"
                                                        value={checkClaimsFilters.client_email}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Email du client"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Code sinistre</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="claim_code"
                                                        value={checkClaimsFilters.claim_code}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Code du sinistre"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Partenaire</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="company_name"
                                                        value={checkClaimsFilters.company_name}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Nom de société"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Nom référence</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="reference_name"
                                                        value={checkClaimsFilters.reference_name}
                                                        onChange={handleCheckClaimsFilterChange}
                                                        placeholder="Nom de la référence"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                <div className="d-flex justify-content-end gap-2 mb-3">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => loadCheckClaims(checkClaimsPage)}
                                        disabled={checkClaimsState.loading}
                                        className="d-flex align-items-center"
                                    >
                                        <FiRefreshCw className={`me-2 ${checkClaimsState.loading ? 'spinning' : ''}`} />
                                        Actualiser
                                    </Button>
                                </div>

                                {checkClaimsState.loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Chargement des check claims...</p>
                                    </div>
                                ) : checkClaimsArray.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FiCheckCircle size={48} className="text-muted mb-3" />
                                        <p className="text-muted">Aucun check claim disponible</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <Table hover striped className="admin-table">
                                                <thead>
                                                    <tr>
                                                        <th>Code police</th>
                                                        <th>Numéro réservation</th>
                                                        <th>Email client</th>
                                                        <th>Forecast déclenché</th>
                                                        <th>Forecast vérifié</th>
                                                        <th>Date fin activité</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {checkClaimsArray.map((item, idx) => (
                                                        <tr key={item.check_claim_id || item.forecast_id || idx}>
                                                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                {getDeep(item, ['policy_info', 'policy_code'], 'N/A')}
                                                            </td>
                                                            <td>{getDeep(item, ['activity_info', 'reservation_number'], 'N/A')}</td>
                                                            <td>{getDeep(item, ['client_info', 'email'], 'N/A')}</td>
                                                            <td>
                                                                <Badge bg={getDeep(item, ['is_triggered_forecast']) ? 'success' : 'secondary'}>
                                                                    {getDeep(item, ['is_triggered_forecast']) ? 'Oui' : 'Non'}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <Badge bg={getDeep(item, ['forecast_checked']) === 'not created date' || getDeep(item, ['forecast_checked']) === false ? 'warning' : 'success'}>
                                                                    {getDeep(item, ['forecast_checked']) === 'not created date' || getDeep(item, ['forecast_checked']) === false ? 'Non vérifié' : 'Vérifié'}
                                                                </Badge>
                                                            </td>
                                                            <td>{getDeep(item, ['activity_info', 'activity_end_date'], 'N/A')}</td>
                                                            <td>
                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        const checkClaimId = item.check_claim_id || item.forecast_id;
                                                                        if (checkClaimId) {
                                                                            navigate(`/admin/trips/check-claims/${checkClaimId}`, {
                                                                                state: { checkClaim: item }
                                                                            });
                                                                        }
                                                                    }}
                                                                    title="Détails"
                                                                >
                                                                    <FiEye />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Pagination */}
                                        {checkClaimsTotalItems > 0 && checkClaimsTotalPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-4">
                                                <div className="text-muted">
                                                    Affichage de {checkClaimsIndexOfFirstItem + 1} à {checkClaimsIndexOfLastItem} sur {checkClaimsTotalItems} check claim{checkClaimsTotalItems > 1 ? 's' : ''}
                                                </div>
                                                <Pagination className="mb-0">
                                                    <Pagination.First
                                                        onClick={() => {
                                                            setCheckClaimsPage(1);
                                                            loadCheckClaims(1);
                                                        }}
                                                        disabled={checkClaimsPage === 1}
                                                    />
                                                    <Pagination.Prev
                                                        onClick={() => {
                                                            const prevPage = checkClaimsPage - 1;
                                                            setCheckClaimsPage(prevPage);
                                                            loadCheckClaims(prevPage);
                                                        }}
                                                        disabled={checkClaimsPage === 1}
                                                    />
                                                    {Array.from({ length: Math.min(5, checkClaimsTotalPages) }, (_, i) => {
                                                        const page = i + 1;
                                                        return (
                                                            <Pagination.Item
                                                                key={page}
                                                                active={page === checkClaimsPage}
                                                                onClick={() => {
                                                                    setCheckClaimsPage(page);
                                                                    loadCheckClaims(page);
                                                                }}
                                                            >
                                                                {page}
                                                            </Pagination.Item>
                                                        );
                                                    })}
                                                    <Pagination.Next
                                                        onClick={() => {
                                                            const nextPage = checkClaimsPage + 1;
                                                            setCheckClaimsPage(nextPage);
                                                            loadCheckClaims(nextPage);
                                                        }}
                                                        disabled={checkClaimsPage === checkClaimsTotalPages}
                                                    />
                                                    <Pagination.Last
                                                        onClick={() => {
                                                            setCheckClaimsPage(checkClaimsTotalPages);
                                                            loadCheckClaims(checkClaimsTotalPages);
                                                        }}
                                                        disabled={checkClaimsPage === checkClaimsTotalPages}
                                                    />
                                                </Pagination>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="rib-form">
                                {ribError && (
                                    <Alert variant="danger" dismissible onClose={() => setRibError(null)} className="mb-3">
                                        {ribError}
                                    </Alert>
                                )}

                                <Form onSubmit={handleRibSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    Code de police <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={policyCode}
                                                    onChange={(e) => setPolicyCode(e.target.value)}
                                                    placeholder="Ex: 123456789"
                                                    required
                                                    disabled={ribLoading}
                                                />
                                                <Form.Text className="text-muted">
                                                    Le type de distribution est déterminé automatiquement depuis la sous-couverture du séjour.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="d-flex gap-2 mb-3">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={ribLoading || !policyCode.trim()}
                                        >
                                            {ribLoading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" animation="border" />
                                                    Génération...
                                                </>
                                            ) : (
                                                <>
                                                    <FiLink className="me-2" />
                                                    Générer le lien
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={handleRibReset}
                                            disabled={ribLoading}
                                        >
                                            Réinitialiser
                                        </Button>
                                    </div>
                                </Form>

                                {ribUrl && (
                                    <Card className="mt-4" style={{ backgroundColor: '#f8f9fa' }}>
                                        <Card.Body>
                                            <h5 className="mb-3">
                                                <FiLink className="me-2" />
                                                Lien RIB généré
                                            </h5>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    value={ribUrl}
                                                    readOnly
                                                    style={{ backgroundColor: 'white' }}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleRibCopy}
                                                    title="Copier le lien"
                                                >
                                                    {copied ? (
                                                        <>
                                                            <FiCheck className="me-2" />
                                                            Copié !
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiCopy className="me-2" />
                                                            Copier
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    onClick={handleRibOpenLink}
                                                    title="Ouvrir le lien dans un nouvel onglet"
                                                >
                                                    <FiLink className="me-2" />
                                                    Ouvrir
                                                </Button>
                                            </InputGroup>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="bulk-transfers">
                                <p className="text-muted mb-3">
                                    Génère deux fichiers : CSV pour les virements et Excel pour l'import paid daily
                                </p>

                                <Tab.Container activeKey={bulkTransfersActiveTab} onSelect={(k) => {
                                    setBulkTransfersActiveTab(k || 'daily');
                                    if (k === 'daily') {
                                        setBulkTransfersMethod('byDate');
                                    } else if (k === 'endOfStay') {
                                        setBulkTransfersMethod('endOfStay');
                                    }
                                }}>
                                    <Nav variant="tabs" className="mb-3">
                                        <Nav.Item>
                                            <Nav.Link eventKey="daily">Daily</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="endOfStay">End of Stay</Nav.Link>
                                        </Nav.Item>
                                    </Nav>

                                    <Tab.Content>
                                        <Tab.Pane eventKey="daily">
                                            <Card className="mb-3">
                                                <Card.Header>
                                                    <h5 className="mb-0">Méthode de génération - Daily</h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="radio"
                                                            label="Par date de claim"
                                                            name="method-daily"
                                                            value="byDate"
                                                            checked={bulkTransfersMethod === 'byDate'}
                                                            onChange={(e) => setBulkTransfersMethod(e.target.value)}
                                                            id="method-by-date"
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="radio"
                                                            label="Par codes de police"
                                                            name="method-daily"
                                                            value="byPolicyCodes"
                                                            checked={bulkTransfersMethod === 'byPolicyCodes'}
                                                            onChange={(e) => setBulkTransfersMethod(e.target.value)}
                                                            id="method-by-codes"
                                                        />
                                                    </Form.Group>
                                                </Card.Body>
                                            </Card>

                                            {bulkTransfersMethod === 'byDate' && (
                                                <Card className="mb-4">
                                                    <Card.Header>
                                                        <h5 className="mb-0">
                                                            <FiCalendar className="me-2" />
                                                            Génération par date
                                                        </h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Date de la claim (optionnel)</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={claimDate}
                                                                onChange={(e) => setClaimDate(e.target.value)}
                                                                placeholder="YYYY-MM-DD"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Si non fournie, utilise la date du jour. Format: YYYY-MM-DD
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Button
                                                            variant="primary"
                                                            onClick={handleGenerateByDate}
                                                            disabled={generateBulkTransfersCsv.loading}
                                                            className="mt-3"
                                                        >
                                                            {generateBulkTransfersCsv.loading ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Génération en cours...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiDownload className="me-2" />
                                                                    Générer les fichiers
                                                                </>
                                                            )}
                                                        </Button>

                                                        {generateBulkTransfersCsv.error && (
                                                            <Alert variant="danger" className="mt-3">
                                                                {generateBulkTransfersCsv.error}
                                                            </Alert>
                                                        )}

                                                        {generateBulkTransfersCsv.success && generateBulkTransfersCsv.data && (
                                                            <Alert variant={generateBulkTransfersCsv.data.total_records === 0 ? "info" : "success"} className="mt-3">
                                                                {generateBulkTransfersCsv.data.message ? (
                                                                    <>
                                                                        <strong>{generateBulkTransfersCsv.data.message}</strong>
                                                                        <FileDownloadSection data={generateBulkTransfersCsv.data} />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <strong>Fichiers générés avec succès !</strong>
                                                                        <br />
                                                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'daily'.
                                                                        <FileDownloadSection data={generateBulkTransfersCsv.data} />
                                                                    </>
                                                                )}
                                                            </Alert>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {bulkTransfersMethod === 'byPolicyCodes' && (
                                                <Card className="mb-4">
                                                    <Card.Header>
                                                        <h5 className="mb-0">
                                                            <FiFileText className="me-2" />
                                                            Génération par codes de police
                                                        </h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Codes de police (requis)</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={3}
                                                                value={policyCodes}
                                                                onChange={(e) => setPolicyCodes(e.target.value)}
                                                                placeholder="Entrez les codes de police séparés par des virgules. Exemple: 658204658, 123456789"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Séparez les codes par des virgules. Exemple: 658204658, 123456789
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Date de la claim (optionnel)</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={claimDateByCodes}
                                                                onChange={(e) => setClaimDateByCodes(e.target.value)}
                                                                placeholder="YYYY-MM-DD"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Si non fournie, utilise la date du jour. Format: YYYY-MM-DD
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Button
                                                            variant="primary"
                                                            onClick={handleGenerateByPolicyCodes}
                                                            disabled={generateBulkTransfersCsvByPolicyCodes.loading}
                                                            className="mt-3"
                                                        >
                                                            {generateBulkTransfersCsvByPolicyCodes.loading ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Génération en cours...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiDownload className="me-2" />
                                                                    Générer les fichiers
                                                                </>
                                                            )}
                                                        </Button>

                                                        {generateBulkTransfersCsvByPolicyCodes.error && (
                                                            <Alert variant="danger" className="mt-3">
                                                                {generateBulkTransfersCsvByPolicyCodes.error}
                                                            </Alert>
                                                        )}

                                                        {generateBulkTransfersCsvByPolicyCodes.success && generateBulkTransfersCsvByPolicyCodes.data && (
                                                            <Alert variant="success" className="mt-3">
                                                                <strong>Fichiers générés avec succès !</strong>
                                                                <br />
                                                                Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'daily'.
                                                                <FileDownloadSection data={generateBulkTransfersCsvByPolicyCodes.data} />
                                                            </Alert>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Tab.Pane>

                                        <Tab.Pane eventKey="endOfStay">
                                            <Card className="mb-3">
                                                <Card.Header>
                                                    <h5 className="mb-0">Méthode de génération - End of Stay</h5>
                                                </Card.Header>
                                                <Card.Body>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="radio"
                                                            label="End of stay - Par date de fin de voyage"
                                                            name="method-end-of-stay"
                                                            value="endOfStay"
                                                            checked={bulkTransfersMethod === 'endOfStay'}
                                                            onChange={(e) => setBulkTransfersMethod(e.target.value)}
                                                            id="method-end-of-stay"
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="radio"
                                                            label="End of stay - Par codes de police"
                                                            name="method-end-of-stay"
                                                            value="endOfStayByPolicyCodes"
                                                            checked={bulkTransfersMethod === 'endOfStayByPolicyCodes'}
                                                            onChange={(e) => setBulkTransfersMethod(e.target.value)}
                                                            id="method-end-of-stay-by-codes"
                                                        />
                                                    </Form.Group>
                                                </Card.Body>
                                            </Card>

                                            {bulkTransfersMethod === 'endOfStay' && (
                                                <Card className="mb-4">
                                                    <Card.Header>
                                                        <h5 className="mb-0">
                                                            <FiCalendar className="me-2" />
                                                            Génération End of Stay - Par date de fin de voyage
                                                        </h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Date de fin de voyage (from) - optionnel</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={tripEndDateFrom}
                                                                onChange={(e) => setTripEndDateFrom(e.target.value)}
                                                                placeholder="YYYY-MM-DD"
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Date de fin de voyage (to) - optionnel</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={tripEndDateTo}
                                                                onChange={(e) => setTripEndDateTo(e.target.value)}
                                                                placeholder="YYYY-MM-DD"
                                                            />
                                                        </Form.Group>

                                                        <Button
                                                            variant="primary"
                                                            onClick={handleGenerateEndOfStay}
                                                            disabled={generateBulkTransfersCsvEndOfStay.loading}
                                                            className="mt-3"
                                                        >
                                                            {generateBulkTransfersCsvEndOfStay.loading ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Génération en cours...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiDownload className="me-2" />
                                                                    Générer les fichiers
                                                                </>
                                                            )}
                                                        </Button>

                                                        {generateBulkTransfersCsvEndOfStay.error && (
                                                            <Alert variant="danger" className="mt-3">
                                                                {generateBulkTransfersCsvEndOfStay.error}
                                                            </Alert>
                                                        )}

                                                        {generateBulkTransfersCsvEndOfStay.success && generateBulkTransfersCsvEndOfStay.data && (
                                                            <Alert variant={generateBulkTransfersCsvEndOfStay.data.total_records === 0 ? "info" : "success"} className="mt-3">
                                                                {generateBulkTransfersCsvEndOfStay.data.message ? (
                                                                    <>
                                                                        <strong>{generateBulkTransfersCsvEndOfStay.data.message}</strong>
                                                                        <FileDownloadSection data={generateBulkTransfersCsvEndOfStay.data} />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <strong>Fichiers générés avec succès !</strong>
                                                                        <br />
                                                                        Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'end_of_stay'.
                                                                        <FileDownloadSection data={generateBulkTransfersCsvEndOfStay.data} />
                                                                    </>
                                                                )}
                                                            </Alert>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {bulkTransfersMethod === 'endOfStayByPolicyCodes' && (
                                                <Card className="mb-4">
                                                    <Card.Header>
                                                        <h5 className="mb-0">
                                                            <FiFileText className="me-2" />
                                                            Génération End of Stay - Par codes de police
                                                        </h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Codes de police (requis)</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={3}
                                                                value={policyCodesEndOfStay}
                                                                onChange={(e) => setPolicyCodesEndOfStay(e.target.value)}
                                                                placeholder="Entrez les codes de police séparés par des virgules. Exemple: 658204658, 123456789"
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Séparez les codes par des virgules. Exemple: 658204658, 123456789
                                                            </Form.Text>
                                                        </Form.Group>

                                                        <Button
                                                            variant="primary"
                                                            onClick={handleGenerateEndOfStayByPolicyCodes}
                                                            disabled={generateBulkTransfersCsvEndOfStayByPolicyCodes.loading}
                                                            className="mt-3"
                                                        >
                                                            {generateBulkTransfersCsvEndOfStayByPolicyCodes.loading ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Génération en cours...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiDownload className="me-2" />
                                                                    Générer les fichiers
                                                                </>
                                                            )}
                                                        </Button>

                                                        {generateBulkTransfersCsvEndOfStayByPolicyCodes.error && (
                                                            <Alert variant="danger" className="mt-3">
                                                                {generateBulkTransfersCsvEndOfStayByPolicyCodes.error}
                                                            </Alert>
                                                        )}

                                                        {generateBulkTransfersCsvEndOfStayByPolicyCodes.success && generateBulkTransfersCsvEndOfStayByPolicyCodes.data && (
                                                            <Alert variant="success" className="mt-3">
                                                                <strong>Fichiers générés avec succès !</strong>
                                                                <br />
                                                                Les fichiers ont été uploadés dans le bucket S3 'claim-bulk-transfer' avec sous-dossier 'end_of_stay'.
                                                                <FileDownloadSection data={generateBulkTransfersCsvEndOfStayByPolicyCodes.data} />
                                                            </Alert>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            </Tab.Pane>

                            <Tab.Pane eventKey="standard-bulk-transfers">
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <span className="nav-link active" aria-current="page">End of Stay</span>
                                    </li>
                                </ul>
                                <StandardEndOfStayTransferPanel
                                    renderFiles={(data) => <FileDownloadSection data={data} />}
                                />
                            </Tab.Pane>

                            <Tab.Pane eventKey="claims-management">
                                {importLoading && (
                                    <Alert variant="info" className="mb-3">
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter.
                                    </Alert>
                                )}

                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>Instructions</h5>
                                        <p>Cette page permet d'importer des mises à jour de sinistres (claims) depuis un fichier Excel.</p>
                                        <ul>
                                            <li>Sélectionnez le type d'import (Flower, Olela ou B2C)</li>
                                            <li>Choisissez le fichier Excel contenant les données à importer</li>
                                            <li>Cliquez sur "Importer" pour lancer l'import</li>
                                        </ul>
                                        <p className="text-muted mb-1">
                                            <strong>Formats acceptés:</strong> .xlsx, .xls, .csv
                                        </p>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Importer un fichier Excel</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={(e) => {
                                            e.preventDefault();
                                            handleClaimsManagementImport();
                                        }}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-bold">
                                                            Type d'import <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Select
                                                            value={claimsManagementImportType}
                                                            onChange={(e) => {
                                                                setClaimsManagementImportType(e.target.value);
                                                                setImportError(null);
                                                            }}
                                                            disabled={importLoading}
                                                            required
                                                        >
                                                            <option value="">Sélectionner un type d'import</option>
                                                            <option value="olela">Olela Claims</option>
                                                            <option value="flower">Flower Claims</option>
                                                            <option value="b2c">B2C Claims</option>
                                                        </Form.Select>
                                                        <Form.Text className="text-muted">
                                                            Sélectionnez le type de mises à jour de sinistres à importer
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-bold">
                                                            Fichier Excel <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="file"
                                                            accept=".xlsx,.xls,.csv"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    setImportFile(file);
                                                                    setImportError(null);
                                                                }
                                                            }}
                                                            disabled={importLoading}
                                                            required
                                                        />
                                                        <Form.Text className="text-muted d-block mt-2">
                                                            Formats acceptés: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>
                                                        </Form.Text>
                                                        {importFile && (
                                                            <div className="mt-3 p-2 bg-light rounded">
                                                                <small className="text-success">
                                                                    <strong>✓ Fichier sélectionné :</strong> {importFile.name}
                                                                    <br />
                                                                    <span className="text-muted">Taille : {(importFile.size / 1024 / 1024).toFixed(2)} MB</span>
                                                                </small>
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <div className="d-flex justify-content-end gap-2 mt-4">
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setImportFile(null);
                                                        setClaimsManagementImportType('');
                                                        setImportError(null);
                                                        setImportSuccess(null);
                                                    }}
                                                    disabled={importLoading}
                                                    type="button"
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button
                                                    variant="success"
                                                    type="submit"
                                                    disabled={importLoading || !importFile || !claimsManagementImportType || claimsManagementImportType.trim() === ''}
                                                >
                                                    {importLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            Import en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiUpload className="me-2" />
                                                            Importer
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>

                                {importError && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setImportError(null)}>
                                        <strong>Erreur :</strong> {importError}
                                    </Alert>
                                )}
                                {importSuccess && (
                                    <Alert variant="success" className="mt-3" dismissible onClose={() => setImportSuccess(null)}>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            marginTop: 0,
                                            marginBottom: 0,
                                            fontSize: '0.9rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontFamily: 'inherit'
                                        }}>{importSuccess}</pre>
                                    </Alert>
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey="generic-payment-import">
                                {genericPaymentLoading && (
                                    <Alert variant="info" className="mb-3">
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter.
                                    </Alert>
                                )}

                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>Import paid daily — tous partenaires</h5>
                                        <p className="mb-2">
                                            Met à jour les sinistres depuis un fichier Excel (import paid daily) pour{' '}
                                            <strong>tous les partenaires</strong> via{' '}
                                            <code>PUT /api/trips/claims/bulk-updates/payments</code>.
                                        </p>
                                        <p className="mb-2">
                                            <strong>Colonnes requises :</strong>{' '}
                                            <code>policy_code</code>, <code>reservation_number</code>,{' '}
                                            <code>client_email</code>, <code>client_first_name</code>,{' '}
                                            <code>client_last_name</code>, <code>claim_status</code>
                                        </p>
                                        <p className="mb-2">
                                            <strong>Colonnes optionnelles :</strong> <code>iban</code>, <code>bic</code>
                                        </p>
                                        <p className="mb-2">
                                            <strong>Filtre optionnel :</strong> <code>company_name</code> (multi-valeurs, ex.{' '}
                                            <code>?company_name=Flower&amp;company_name=Aloa</code>). Si absent, tous les partenaires sont traités.
                                        </p>
                                        <p className="text-muted mb-0">
                                            Lorsque <code>claim_status</code> = <code>Paid</code>, un e-mail générique Orakle Weather
                                            est envoyé dans la langue de la police (<code>preferred_language</code> : fr-FR, en-US, de-DE, nl-NL, es-ES).
                                            Objet : « {`{company_name}`} - Votre remboursement a été effectué » (traduit).
                                        </p>
                                        <div className="mt-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={CLAIM_STATUS_PAID_IMPORT_TEMPLATE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                as="a"
                                                className="d-inline-flex align-items-center"
                                            >
                                                <FiDownload className="me-2" />
                                                Télécharger le template
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Importer un fichier Excel</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={(e) => {
                                            e.preventDefault();
                                            handleGenericPaymentImport();
                                        }}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold">
                                                    Filtrer par partenaire (optionnel)
                                                </Form.Label>
                                                <Form.Text className="text-muted d-block mb-2">
                                                    Laissez vide pour traiter tous les partenaires du fichier.
                                                </Form.Text>
                                                <Form.Control
                                                    type="text"
                                                    value={genericPaymentCompanySearch}
                                                    onChange={(e) => setGenericPaymentCompanySearch(e.target.value)}
                                                    placeholder="Rechercher un partenaire…"
                                                    disabled={genericPaymentLoading || forecastPartnersLoading}
                                                    className="mb-2"
                                                />
                                                <div
                                                    className="border rounded p-2 bg-white"
                                                    style={{ maxHeight: '180px', overflowY: 'auto' }}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="generic-payment-partner-all"
                                                        label="Tous les partenaires"
                                                        checked={genericPaymentCompanyNames.length === 0}
                                                        disabled={genericPaymentLoading || forecastPartnersLoading}
                                                        onChange={() => setGenericPaymentCompanyNames([])}
                                                        className="mb-2 fw-semibold"
                                                    />
                                                    {genericPaymentFilteredPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                        <div className="text-muted small px-1 py-2">Aucun partenaire trouvé.</div>
                                                    ) : (
                                                        genericPaymentFilteredPartnerOptions.map((companyName, index) => (
                                                            <Form.Check
                                                                key={`generic-payment-partner-${companyName}-${index}`}
                                                                type="checkbox"
                                                                id={`generic-payment-partner-${companyName}-${index}`}
                                                                label={companyName}
                                                                checked={genericPaymentCompanyNames.includes(companyName)}
                                                                disabled={genericPaymentLoading || forecastPartnersLoading}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGenericPaymentCompanyNames((prev) => [...prev, companyName]);
                                                                    } else {
                                                                        setGenericPaymentCompanyNames((prev) => prev.filter((name) => name !== companyName));
                                                                    }
                                                                }}
                                                                className="mb-1"
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                                {genericPaymentCompanyNames.length > 0 && (
                                                    <div className="mt-2 d-flex flex-wrap gap-1">
                                                        {genericPaymentCompanyNames.map((name) => (
                                                            <Badge bg="secondary" key={`generic-payment-selected-${name}`}>{name}</Badge>
                                                        ))}
                                                    </div>
                                                )}
                                                {forecastPartnersLoading && (
                                                    <div className="text-muted small mt-1">Chargement des partenaires…</div>
                                                )}
                                                {forecastPartnersError && (
                                                    <Alert variant="danger" className="mt-2 mb-0 py-2">{forecastPartnersError}</Alert>
                                                )}
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">
                                                    Fichier Excel <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            setGenericPaymentFile(file);
                                                            setGenericPaymentError(null);
                                                        }
                                                    }}
                                                    disabled={genericPaymentLoading}
                                                    required
                                                />
                                                <Form.Text className="text-muted d-block mt-2">
                                                    Formats acceptés: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>
                                                </Form.Text>
                                                {genericPaymentFile && (
                                                    <div className="mt-3 p-2 bg-light rounded">
                                                        <small className="text-success">
                                                            <strong>✓ Fichier sélectionné :</strong> {genericPaymentFile.name}
                                                            <br />
                                                            <span className="text-muted">
                                                                Taille : {(genericPaymentFile.size / 1024 / 1024).toFixed(2)} MB
                                                            </span>
                                                        </small>
                                                    </div>
                                                )}
                                            </Form.Group>
                                            <div className="d-flex justify-content-end gap-2 mt-4">
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setGenericPaymentFile(null);
                                                        setGenericPaymentCompanyNames([]);
                                                        setGenericPaymentCompanySearch('');
                                                        setGenericPaymentError(null);
                                                        setGenericPaymentSuccess(null);
                                                    }}
                                                    disabled={genericPaymentLoading}
                                                    type="button"
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button
                                                    variant="success"
                                                    type="submit"
                                                    disabled={genericPaymentLoading || !genericPaymentFile}
                                                >
                                                    {genericPaymentLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            Import en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiUpload className="me-2" />
                                                            Importer
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>

                                {genericPaymentError && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setGenericPaymentError(null)}>
                                        <strong>Erreur :</strong> {genericPaymentError}
                                    </Alert>
                                )}
                                {genericPaymentSuccess && (
                                    <Alert variant="success" className="mt-3" dismissible onClose={() => setGenericPaymentSuccess(null)}>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            marginTop: 0,
                                            marginBottom: 0,
                                            fontSize: '0.9rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontFamily: 'inherit'
                                        }}>{genericPaymentSuccess}</pre>
                                    </Alert>
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey="standard-payment-import">
                                {standardPaymentLoading && (
                                    <Alert variant="info" className="mb-3">
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter.
                                    </Alert>
                                )}

                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>Import paid daily — partenaires Standard</h5>
                                        <p className="mb-2">
                                            Met à jour les sinistres depuis un fichier Excel (import paid daily) pour les partenaires
                                            du <strong>périmètre Standard</strong> uniquement via{' '}
                                            <code>PUT /api/standard/trips/claims/bulk-updates/payments</code>.
                                        </p>
                                        <p className="mb-2">
                                            <strong>Colonnes requises :</strong>{' '}
                                            <code>policy_code</code>, <code>reservation_number</code>,{' '}
                                            <code>client_email</code>, <code>client_first_name</code>,{' '}
                                            <code>client_last_name</code>, <code>claim_status</code>
                                        </p>
                                        <p className="mb-2">
                                            <strong>Colonnes optionnelles :</strong> <code>iban</code>, <code>bic</code>
                                        </p>
                                        <p className="mb-2">
                                            <strong>Filtre optionnel :</strong> <code>company_name</code> (multi-valeurs, ex.{' '}
                                            <code>?company_name=Flower&amp;company_name=Aloa</code>). Si absent, tous les partenaires Standard sont traités.
                                        </p>
                                        <p className="text-muted mb-0">
                                            Lorsque <code>claim_status</code> = <code>Paid</code>, envoi de l&apos;e-mail client
                                            « Votre virement est en cours » (style GARANTIE MÉTÉO / INDEMNISATION) avec logo partenaire.
                                        </p>
                                        <div className="mt-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={CLAIM_STATUS_PAID_IMPORT_TEMPLATE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                as="a"
                                                className="d-inline-flex align-items-center"
                                            >
                                                <FiDownload className="me-2" />
                                                Télécharger le template
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Importer un fichier Excel</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={(e) => {
                                            e.preventDefault();
                                            handleStandardPaymentImport();
                                        }}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold">
                                                    Filtrer par partenaire (optionnel)
                                                </Form.Label>
                                                <Form.Text className="text-muted d-block mb-2">
                                                    Laissez vide pour traiter tous les partenaires Standard du fichier.
                                                </Form.Text>
                                                <Form.Control
                                                    type="text"
                                                    value={standardPaymentCompanySearch}
                                                    onChange={(e) => setStandardPaymentCompanySearch(e.target.value)}
                                                    placeholder="Rechercher un partenaire…"
                                                    disabled={standardPaymentLoading || forecastPartnersLoading}
                                                    className="mb-2"
                                                />
                                                <div
                                                    className="border rounded p-2 bg-white"
                                                    style={{ maxHeight: '180px', overflowY: 'auto' }}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="standard-payment-partner-all"
                                                        label="Tous les partenaires"
                                                        checked={standardPaymentCompanyNames.length === 0}
                                                        disabled={standardPaymentLoading || forecastPartnersLoading}
                                                        onChange={() => setStandardPaymentCompanyNames([])}
                                                        className="mb-2 fw-semibold"
                                                    />
                                                    {standardPaymentFilteredPartnerOptions.length === 0 && !forecastPartnersLoading ? (
                                                        <div className="text-muted small px-1 py-2">Aucun partenaire trouvé.</div>
                                                    ) : (
                                                        standardPaymentFilteredPartnerOptions.map((companyName, index) => (
                                                            <Form.Check
                                                                key={`standard-payment-partner-${companyName}-${index}`}
                                                                type="checkbox"
                                                                id={`standard-payment-partner-${companyName}-${index}`}
                                                                label={companyName}
                                                                checked={standardPaymentCompanyNames.includes(companyName)}
                                                                disabled={standardPaymentLoading || forecastPartnersLoading}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setStandardPaymentCompanyNames((prev) => [...prev, companyName]);
                                                                    } else {
                                                                        setStandardPaymentCompanyNames((prev) => prev.filter((name) => name !== companyName));
                                                                    }
                                                                }}
                                                                className="mb-1"
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                                {standardPaymentCompanyNames.length > 0 && (
                                                    <div className="mt-2 d-flex flex-wrap gap-1">
                                                        {standardPaymentCompanyNames.map((name) => (
                                                            <Badge bg="secondary" key={`standard-payment-selected-${name}`}>{name}</Badge>
                                                        ))}
                                                    </div>
                                                )}
                                                {forecastPartnersLoading && (
                                                    <div className="text-muted small mt-1">Chargement des partenaires…</div>
                                                )}
                                                {forecastPartnersError && (
                                                    <Alert variant="danger" className="mt-2 mb-0 py-2">{forecastPartnersError}</Alert>
                                                )}
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">
                                                    Fichier Excel <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            setStandardPaymentFile(file);
                                                            setStandardPaymentError(null);
                                                        }
                                                    }}
                                                    disabled={standardPaymentLoading}
                                                    required
                                                />
                                                <Form.Text className="text-muted d-block mt-2">
                                                    Formats acceptés: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong>
                                                </Form.Text>
                                                {standardPaymentFile && (
                                                    <div className="mt-3 p-2 bg-light rounded">
                                                        <small className="text-success">
                                                            <strong>✓ Fichier sélectionné :</strong> {standardPaymentFile.name}
                                                            <br />
                                                            <span className="text-muted">
                                                                Taille : {(standardPaymentFile.size / 1024 / 1024).toFixed(2)} MB
                                                            </span>
                                                        </small>
                                                    </div>
                                                )}
                                            </Form.Group>
                                            <div className="d-flex justify-content-end gap-2 mt-4">
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setStandardPaymentFile(null);
                                                        setStandardPaymentCompanyNames([]);
                                                        setStandardPaymentCompanySearch('');
                                                        setStandardPaymentError(null);
                                                        setStandardPaymentSuccess(null);
                                                    }}
                                                    disabled={standardPaymentLoading}
                                                    type="button"
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button
                                                    variant="success"
                                                    type="submit"
                                                    disabled={standardPaymentLoading || !standardPaymentFile}
                                                >
                                                    {standardPaymentLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            Import en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiUpload className="me-2" />
                                                            Importer
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>

                                {standardPaymentError && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setStandardPaymentError(null)}>
                                        <strong>Erreur :</strong> {standardPaymentError}
                                    </Alert>
                                )}
                                {standardPaymentSuccess && (
                                    <Alert variant="success" className="mt-3" dismissible onClose={() => setStandardPaymentSuccess(null)}>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            marginTop: 0,
                                            marginBottom: 0,
                                            fontSize: '0.9rem',
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontFamily: 'inherit'
                                        }}>{standardPaymentSuccess}</pre>
                                    </Alert>
                                )}
                            </Tab.Pane>
                            <Tab.Pane eventKey="bulk-reminders-email">
                                <Card className="mb-4">
                                    <Card.Body>
                                        <h5>Relances e-mail — remboursement / coordonnées bancaires</h5>
                                        <p className="mb-2">
                                            Déclenche l’endpoint{' '}
                                            <code>POST /api/trips/clients/bank-details/bulk-reminders-email</code>.
                                        </p>
                                        <p className="text-muted mb-0">
                                            Envoyez un fichier attendu par le backend (ex. liste de polices / clients). Le traitement peut prendre plusieurs minutes.
                                        </p>
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Fichier</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={handleBulkRemindersEmail}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Fichier <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        setBulkRemindersFile(file || null);
                                                        setBulkRemindersError(null);
                                                        setBulkRemindersResult(null);
                                                    }}
                                                    disabled={bulkRemindersLoading}
                                                />
                                                <Form.Text className="text-muted">
                                                    Formats usuels : .xlsx, .xls, .csv (selon contrat API).
                                                </Form.Text>
                                                {bulkRemindersFile && (
                                                    <div className="mt-2 p-2 bg-light rounded small">
                                                        <strong>Fichier :</strong> {bulkRemindersFile.name}
                                                        {' — '}
                                                        {(bulkRemindersFile.size / 1024 / 1024).toFixed(2)} MB
                                                    </div>
                                                )}
                                            </Form.Group>
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        setBulkRemindersFile(null);
                                                        setBulkRemindersError(null);
                                                        setBulkRemindersResult(null);
                                                    }}
                                                    disabled={bulkRemindersLoading}
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button type="submit" variant="primary" disabled={bulkRemindersLoading || !bulkRemindersFile}>
                                                    {bulkRemindersLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" animation="border" />
                                                            Envoi en cours...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiMail className="me-2" />
                                                            Envoyer les relances
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                                {bulkRemindersError && (
                                    <Alert variant="danger" className="mt-3" dismissible onClose={() => setBulkRemindersError(null)}>
                                        <strong>Erreur :</strong> {bulkRemindersError}
                                    </Alert>
                                )}
                                {bulkRemindersResult !== null && bulkRemindersResult !== undefined && (
                                    <Alert
                                        variant="success"
                                        className="mt-3"
                                        dismissible
                                        onClose={() => setBulkRemindersResult(null)}
                                    >
                                        <p className="mb-2 fw-semibold d-flex align-items-center gap-2 text-success">
                                            <FiCheckCircle aria-hidden />
                                            Requête terminée
                                        </p>
                                        {typeof bulkRemindersResult === 'object' && bulkRemindersResult !== null ? (
                                            <pre
                                                className="mb-0 small"
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    maxHeight: '320px',
                                                    overflow: 'auto',
                                                    background: 'rgba(255,255,255,0.6)',
                                                    padding: '10px',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                {JSON.stringify(bulkRemindersResult, null, 2)}
                                            </pre>
                                        ) : (
                                            <span>{String(bulkRemindersResult)}</span>
                                        )}
                                    </Alert>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsClaims;

// Claim modals (details/edit/delete)
// Positioned after the default export to avoid cluttering the main render; could be refactored into components later.
